import { Vector3 } from 'three'
import type { Bus } from './bus'
import type { BusSplineFollower } from './busSplineFollower'

/**
 * Records what a bus's heading actually does while it merges, so a visible
 * twitch can be traced to the frame and the distance it happened at.
 *
 * Turned on with `?trace` in the URL, or `busTrace(true)` from the console. It
 * samples heading frame by frame and reports the sharpest change, so a kink
 * shows up as a spike in one row rather than something to catch by eye.
 */

export interface MergeSample {
  /** Seconds since the merge began. */
  t: number
  /** Arc distance along whichever curve the bus is on. */
  distance: number
  phase: 'entry' | 'road'
  /** Heading in degrees, so successive rows can be differenced. */
  heading: number
  /** Change in heading since the previous sample, degrees. */
  delta: number
  /** How far the bus moved since the previous sample. */
  step: number
}

let enabled = false

export function busTrace(on = true): void {
  enabled = on
  console.log(`[merge] tracing ${on ? 'on' : 'off'}`)
}

if (typeof location !== 'undefined' && location.search.includes('trace')) enabled = true
if (typeof window !== 'undefined') {
  ;(window as unknown as { busTrace: typeof busTrace }).busTrace = busTrace
}

export class MergeTrace {
  private readonly samples = new Map<number, MergeSample[]>()
  private readonly last = new Map<number, { heading: number; position: Vector3 }>()
  private readonly elapsed = new Map<number, number>()
  private readonly reported = new Set<number>()

  get on(): boolean {
    return enabled
  }

  /** Call once per frame per bus, after its transform has been updated. */
  sample(bus: Bus, follower: BusSplineFollower, position: Vector3, dt: number): void {
    if (!enabled) return

    const phase: 'entry' | 'road' = follower.isOnEntryPath ? 'entry' : 'road'
    const previous = this.last.get(bus.id)

    const forward = new Vector3(0, 0, -1).applyQuaternion(bus.rotation)
    forward.y = 0
    forward.normalize()
    const heading = (Math.atan2(forward.x, -forward.z) * 180) / Math.PI

    const t = (this.elapsed.get(bus.id) ?? 0) + dt
    this.elapsed.set(bus.id, t)

    if (previous) {
      let delta = heading - previous.heading
      while (delta > 180) delta -= 360
      while (delta < -180) delta += 360

      let rows = this.samples.get(bus.id)
      if (!rows) {
        rows = []
        this.samples.set(bus.id, rows)
      }
      rows.push({
        t,
        distance: follower.headDistance,
        phase,
        heading,
        delta,
        step: position.distanceTo(previous.position),
      })
    }

    this.last.set(bus.id, { heading, position: position.clone() })

    // Report once the bus has settled onto the road.
    if (phase === 'road' && !this.reported.has(bus.id) && (this.samples.get(bus.id)?.length ?? 0) > 4) {
      this.reported.add(bus.id)
      this.report(bus)
    }
  }

  private report(bus: Bus): void {
    const rows = this.samples.get(bus.id) ?? []
    if (rows.length === 0) return

    // Ignore the first couple of frames: the bus is settling into the curve.
    const body = rows.slice(2)
    let worst = body[0]
    for (const row of body) {
      if (Math.abs(row.delta) > Math.abs(worst.delta)) worst = row
    }

    const handover = body.findIndex((r, i) => i > 0 && r.phase === 'road' && body[i - 1].phase === 'entry')

    console.groupCollapsed(
      `[merge] bus #${bus.id} — ${rows.length} frames, sharpest heading change ${worst.delta.toFixed(1)}°/frame` +
        ` at t=${worst.t.toFixed(2)}s (${worst.phase})`,
    )
    console.log(
      'handover at row',
      handover < 0 ? '(none seen)' : handover,
      handover >= 0
        ? `— heading changed ${body[handover].delta.toFixed(1)}°, moved ${body[handover].step.toFixed(2)}u that frame`
        : '',
    )
    console.table(
      body.map((r) => ({
        t: +r.t.toFixed(3),
        phase: r.phase,
        dist: +r.distance.toFixed(2),
        heading: +r.heading.toFixed(1),
        'Δheading': +r.delta.toFixed(2),
        step: +r.step.toFixed(3),
      })),
    )
    console.groupEnd()
  }

  reset(): void {
    this.samples.clear()
    this.last.clear()
    this.elapsed.clear()
    this.reported.clear()
  }
}
